<template>
  <v-container class="repayment-container">
    <v-card class="elevation-3 rounded-lg pa-4">
      <div class="repayment-faqs">
        <!-- Repayment FAQs Section -->
        <h2 class="text-h5 font-weight-bold primary--text mb-4">
          Loan Repayment FAQs
        </h2>

        <v-expansion-panels flat>
          <!-- How do I repay my loan? -->
          <v-expansion-panel>
            <v-expansion-panel-header
              class="text-subtitle-1 font-weight-bold primary--text"
            >
              How do I repay my loan?
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-text>
                <p class="body-1 mb-3">
                  Your loan repayment will be processed
                  <strong>automatically through the DebiCheck</strong> you
                  authenticated on the due date specified in your
                  <strong>Loan Agreement</strong>. The amount due will be
                  <strong>debited from your bank account</strong> via a secure
                  debit order, so there's no need for manual action unless
                  otherwise arranged.
                </p>

                <v-alert
                  outlined
                  type="info"
                  icon="mdi-calendar-clock"
                  class="mt-4"
                >
                  <p class="body-1 mb-0">
                    <strong>Note:</strong> If your payday falls on a Saturday,
                    Sunday, or public holiday, the debit order will be processed
                    on the <strong>business day prior</strong> to the weekend or
                    public holiday.
                  </p>
                </v-alert>
              </v-card-text>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Can I repay my loan before the due date? -->
          <v-expansion-panel>
            <v-expansion-panel-header
              class="text-subtitle-1 font-weight-bold primary--text"
            >
              Can I repay my loan before the due date?
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-text class="pa-0">
                <p class="body-1 mb-3">
                  Yes, you may settle your loan early at any time. Here's how:
                </p>

                <v-stepper vertical>
                  <v-stepper-step step="1" complete>
                    Click <strong>Log In</strong> on our website
                  </v-stepper-step>
                  <v-stepper-content step="1"> </v-stepper-content>

                  <v-stepper-step step="2" complete>
                    Sign in using your
                    <strong>registered mobile number and password</strong>
                  </v-stepper-step>
                  <v-stepper-content step="2"> </v-stepper-content>

                  <v-stepper-step step="3" complete>
                    Select the <strong>"Early Settlement"</strong> option
                  </v-stepper-step>
                  <v-stepper-content step="3"> </v-stepper-content>

                  <v-stepper-step step="4" complete>
                    The system will generate the
                    <strong>settlement amount</strong> (valid for that day only)
                  </v-stepper-step>
                  <v-stepper-content step="4"> </v-stepper-content>

                  <v-stepper-step step="5" complete>
                    Make payment via <strong>EFT</strong> or your preferred
                    <strong>banking channel</strong>
                  </v-stepper-step>
                  <v-stepper-content step="5"> </v-stepper-content>
                </v-stepper>

                <v-alert type="warning" outlined dense class="mt-4">
                  <strong>Note:</strong> The settlement amount is only valid for
                  5 business days from when it is generated. Ensure payment is
                  made on the <strong>same day</strong>.
                </v-alert>
              </v-card-text>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- How do I repay an overdue loan? -->
          <v-expansion-panel>
            <v-expansion-panel-header
              class="text-subtitle-1 font-weight-bold primary--text"
            >
              How do I repay an overdue loan?
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-text class="pa-0">
                <p class="body-1 mb-3">To settle an overdue loan:</p>

                <v-stepper vertical>
                  <v-stepper-step step="1" complete>
                    Log in using your
                    <strong>mobile number and password</strong>
                  </v-stepper-step>
                  <v-stepper-content step="1"> </v-stepper-content>

                  <v-stepper-step step="2" complete>
                    Select the <strong>"Settle Overdue Amount"</strong> option
                  </v-stepper-step>
                  <v-stepper-content step="2"> </v-stepper-content>

                  <v-stepper-step step="3" complete>
                    View the updated <strong>outstanding balance</strong>,
                    including any applicable fees
                  </v-stepper-step>
                  <v-stepper-content step="3"> </v-stepper-content>

                  <v-stepper-step step="4" complete>
                    Make payment using your
                    <strong>bank or preferred payment method</strong>
                  </v-stepper-step>
                  <v-stepper-content step="4"> </v-stepper-content>
                </v-stepper>

                <v-alert type="warning" outlined dense class="mt-4">
                  <strong>Important</strong>: Overdue settlement amounts are
                  also valid
                  <strong
                    >only for 5 business days from when they are
                    generated</strong
                  >. Ensure timely payment to avoid additional charges.
                </v-alert>
              </v-card-text>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Need help with repayments? -->
          <v-expansion-panel>
            <v-expansion-panel-header
              class="text-subtitle-1 font-weight-bold primary--text"
            >
              Need help with repayments?
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-text class="pa-0">
                <p class="body-1 mb-4">
                  If you're having trouble or have any questions about your
                  repayment options, feel free to reach out:
                </p>

                <v-card outlined class="pa-4 mb-4">
                  <v-row align="center" no-gutters>
                    <v-col cols="12" sm="6" class="mb-3 mb-sm-0">
                      <div class="d-flex align-center">
                        <v-icon left color="primary">mdi-phone</v-icon>
                        <span class="font-weight-medium"
                          ><a
                            href="https://wa.me/+270210541057"
                            class="text-decoration-none font-weight-medium"
                          >
                            +27 021 054 1057
                          </a></span
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <div class="d-flex align-center">
                        <v-icon left color="primary">mdi-email</v-icon>
                        <!-- Mobile Only -->
                        <a
                          href="mailto:clientsupport@centuryfinancialservices.net"
                          class="text-decoration-none font-weight-medium d-inline d-md-none"
                        >
                          clientsupport
                        </a>

                        <!-- Desktop Only -->
                        <a
                          href="mailto:clientsupport@centuryfinancialservices.net"
                          class="text-decoration-none font-weight-medium d-none d-md-inline"
                        >
                          clientsupport@centuryfinancialservices.net
                        </a>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>

                <p class="body-1 text-center font-weight-medium primary--text">
                  We're here to assist!
                </p>
              </v-card-text>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "RepaymentComponent",
  data() {
    return {
      // No specific data items needed for this component currently
    };
  },
};
</script>

<style scoped>
.repayment-container {
  max-width: 900px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 24px;
}
</style>
